/**
 * GameOverShell: M4 batch 6c.
 *
 * Renders for `phase === "gameOver"`. Shows the final scoreboard
 * (aggregated from `turnSummaries`), highlights the winner, and offers
 * a REMATCH button plus a Leave / back-to-home exit and a share-result
 * CTA. Any player can fire the REMATCH frame; the server (any-player
 * gated, gameOver-phase gated) dispatches a fresh gameId and the next
 * ROOM_STATE_SYNC carries everyone back to wordPick.
 *
 * V4 T15: the scoreboard is a TABULAR GRID, `# | name | total | drew`
 *: with tabular figures, an --accent-bordered winner row, and a "drew"
 * column header replacing the old cryptic "(Drawer: N)" string. The UA
 * list marker is killed (list-style:none) so the single explicit rank
 * cell is the ONLY number per row (pre-T15 both rendered → "1. 1.").
 *
 *   ┌──────────────────────────────────────────────┐
 *   │  Game over!                                  │
 *   ├──────────────────────────────────────────────┤
 *   │  🏆 Winner: Alice          2870 pts          │
 *   ├──────────────────────────────────────────────┤
 *   │   #  Player        Total      Drew           │
 *   │   1  Alice          2870      1625   ◀ accent│
 *   │   2  Bob            2300       875            │
 *   │   3  Charlie        1750       250           │
 *   ├──────────────────────────────────────────────┤
 *   │  [ Rematch ]  [ Leave ]   ← scribble buttons │
 *   │  share-result CTA                            │
 *   └──────────────────────────────────────────────┘
 *
 * The "Returning to lobby…" message appears briefly after the user
 * clicks Rematch but before the server's PHASE_CHANGED → wordPick
 * frame lands. The button disables on first click so a stuck-click
 * doesn't double-fire.
 */

import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import type { ClientControlFrame, GameStateView } from "../ws.js";
import { aggregateScoreboard, competitionRanks } from "../game/scoreboard.js";
import { START_LATCH_TIMEOUT_MS } from "./LobbyShell.js";
import { ScribbleButton } from "../components/ScribbleButton.js";
import { GameOverShareRow } from "../components/GameOverShareRow.js";
import { CircleFrame } from "../marks/v1/CircleFrame.js";
import { InkCat } from "../marks/v1/InkCat.js";
import { SCRIBBLE_IN_CLASS } from "../anim/scribble-in.js";
import { PAGE_TEAR_IN_CLASS, PageTearLine } from "../anim/page-tear.js";
import { CountUp } from "../anim/count-up.js";

type GameOverShellProps = {
  /** Cached game-state projection from RoomShell. */
  game: GameStateView;
  /** Player roster: used to look up display names by id. */
  players: ReadonlyArray<{ id: string; name: string }>;
  /** Room code: wires the share-result CTA (funnel, D7). */
  roomId: string;
  /**
   * Local player's id: marks exactly THIS client's row with the coral "you"
   * treatment (mirrors the lobby me-pill). Distinct from the accent winner
   * highlight so a tied non-winner can still find themself. Optional for tests.
   */
  playerId?: string | null;
  /** WS sender. Provided by RoomShell so the click commits over the wire. */
  sendFrame: (frame: ClientControlFrame) => boolean;
  /** Back-to-home / leave exit. RoomShell threads `route("/")` here. */
  onLeave: () => void;
  /**
   * Q9 (audit 2026-06-11): id → name memory for players who left mid-game
   * (RoomShell accumulates it from every roster it has seen). Their rows
   * stay on the final standings, grayed with a "(left)" tag, instead of
   * silently vanishing points-and-all.
   */
  departedNames?: ReadonlyMap<string, string>;
};

export function GameOverShell({
  game,
  players,
  roomId,
  playerId = null,
  sendFrame,
  onLeave,
  departedNames,
}: GameOverShellProps) {
  const [rematchSent, setRematchSent] = useState(false);

  // Build the roster Map the scoreboard helper expects. The departed-aware
  // view is always on at game over: an empty name map still keeps departed
  // scorers ("Someone") rather than dropping them.
  const rosterMap = new Map(players.map((p) => [p.id, p]));
  const scoreboard = aggregateScoreboard(game.turnSummaries, rosterMap, departedNames ?? new Map());

  // Standard competition ranking so tied totals SHARE a rank (1,1,3, not
  // 1,2,3), matching the "It's a tie" banner. Parallel to `scoreboard`.
  const ranks = competitionRanks(scoreboard.map((e) => e.total));

  const winner = scoreboard[0];
  // Audit 2026-06-11: scores quantize to 250s, so ties are common (3 of 5
  // audit games): and the UI used to crown the alphabetically-first player.
  const coWinners = winner ? scoreboard.filter((e) => e.total === winner.total) : [];
  const isTie = coWinners.length > 1;

  // Q9 review fix (adversarial P2): same silent-third-case backstop as the
  // lobby latch: a REMATCH accepted by the socket but lost mid-flight
  // (reconnect lands back in gameOver) stuck the button forever. Success
  // unmounts this shell, which clears the timer.
  const rematchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (rematchTimeoutRef.current !== null) clearTimeout(rematchTimeoutRef.current);
    },
    [],
  );

  const handleRematch = useCallback(() => {
    if (rematchSent) return;
    setRematchSent(true);
    // Q9 sibling sweep (audit 2026-06-11): same latch class as the lobby's
    // "Starting…": a frame dropped while disconnected left the button stuck
    // on "Starting the rematch…" forever. A server-side reject self-heals
    // (the winning rematch routes everyone away), but a silent local drop
    // needs the latch released.
    const sent = sendFrame({ kind: "REMATCH" });
    if (!sent) {
      setRematchSent(false);
      return;
    }
    rematchTimeoutRef.current = setTimeout(() => {
      rematchTimeoutRef.current = null;
      setRematchSent(false);
    }, START_LATCH_TIMEOUT_MS);
  }, [rematchSent, sendFrame]);

  return (
    <div
      class={`game-over-shell ${PAGE_TEAR_IN_CLASS}`}
      data-testid="game-over-shell"
      role="region"
      aria-label="Game over"
    >
      <PageTearLine />
      <h1 class="game-over-heading">Game over!</h1>

      {winner ? (
        <div
          class="game-over-winner"
          data-testid="game-over-winner"
          style={{ "--winner-delay": `${scoreboard.length * 120 + 250}ms` }}
        >
          <span class="game-over-winner-medal">
            <span
              class={`game-over-winner-medal-frame ${SCRIBBLE_IN_CLASS}`}
              data-testid="game-over-winner-medal"
              aria-hidden="true"
            >
              <CircleFrame size={48} />
            </span>
            <span class="game-over-winner-icon" aria-hidden="true">
              🏆
            </span>
          </span>{" "}
          {isTie ? (
            <>
              It's a tie —{" "}
              <strong data-testid="game-over-tie-names">
                {coWinners.map((e) => (e.left ? `${e.name} (left)` : e.name)).join(" & ")}
              </strong>
            </>
          ) : (
            <>
              Winner:{" "}
              <strong>
                {winner.name}
                {winner.left ? " (left)" : ""}
              </strong>
            </>
          )}{" "}
          <span class="game-over-winner-total">
            <CountUp value={winner.total} /> pts
          </span>
        </div>
      ) : (
        <div class="game-over-empty" data-testid="game-over-empty">
          No turns were played.
        </div>
      )}

      {scoreboard.length > 0 && (
        <div class="game-over-scoreboard-block">
          {/*
           * Tabular grid: rank | name (1fr, ellipsis) | total | drew. The
           * header labels the columns: crucially relabelling the old cryptic
           * "(Drawer: N)" to a plain "drew" column. `aria-hidden` because the
           * rows below are self-describing to assistive tech via their text.
           */}
          <div
            class="game-over-row game-over-header"
            data-testid="game-over-header"
            aria-hidden="true"
          >
            <span class="game-over-rank">#</span>
            <span class="game-over-name">Player</span>
            <span class="game-over-total">Total</span>
            <span class="game-over-drew">Drew</span>
          </div>
          {/*
           * list-style:none kills the UA "1." marker so the explicit rank cell
           * is the ONLY number per row (pre-T15 both rendered → "1. 1.").
           */}
          <ol class="game-over-scoreboard" data-testid="game-over-scoreboard">
            {scoreboard.map((entry, i) => (
              <li
                class={`game-over-row${entry.total === winner?.total ? " game-over-row--winner" : ""}${entry.left ? " game-over-row--left" : ""}${playerId != null && entry.playerId === playerId ? " game-over-row--you" : ""}`}
                data-testid={`game-over-row-${i + 1}`}
                key={entry.playerId}
                style={{ "--row-i": scoreboard.length - 1 - i }}
              >
                <span class="game-over-rank">{ranks[i]}</span>
                <span class="game-over-name" title={entry.name}>
                  {entry.name}
                  {entry.left && (
                    <span class="game-over-left-tag" data-testid={`game-over-left-${i + 1}`}>
                      {" "}
                      (left)
                    </span>
                  )}
                </span>
                <span class="game-over-total" data-testid={`game-over-total-${i + 1}`}>
                  <CountUp value={entry.total} />
                </span>
                <span class="game-over-drew" data-testid={`game-over-drew-${i + 1}`}>
                  <CountUp value={entry.drawerPoints} />
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}

      <div class="game-over-actions">
        <ScribbleButton
          onClick={handleRematch}
          disabled={rematchSent}
          variant="primary"
          aria-label="Rematch — start a new game"
        >
          <span data-testid="game-over-rematch-btn">
            {rematchSent ? "Starting the rematch…" : "Rematch"}
          </span>
        </ScribbleButton>
        <ScribbleButton onClick={onLeave} variant="secondary" aria-label="Leave — back to home">
          <span data-testid="game-over-leave-btn">Leave</span>
        </ScribbleButton>
      </div>

      {/* Share-result affordance (funnel, D7), item 9: a single centered line,
          "Room Code: CODE  [Share Room Link]", below the Rematch / Leave row. */}
      <div class="game-over-share">
        <GameOverShareRow roomId={roomId} />
      </div>

      {/* The mascot naps once the game's done (PRD §5.5, sleepy pose, in the
          page flow below the actions so it can never occlude the board). */}
      <div class="game-over-cat" data-testid="game-over-cat">
        <InkCat pose="sleepy" size={64} />
      </div>
    </div>
  );
}
